/**
 * ChallengeSplash.jsx — end-of-challenge result screen.
 * Shows the achieved metrics AGAINST the physics optimum, and the
 * parameter values the student COULD have chosen — the "answer key"
 * of the optimization, shown as a splash (per Dr Rathankar's spec).
 */
import { useGame } from "./store";

const L = 1202;   // track length (m)
const G = 10;
const RHO = 1.2, CD = 0.8, AREA = 2.0;

// Theoretical-optimum estimates per goal (simple physics models)
function optimum(goal, mass) {
  const dragK = 0.5 * RHO * CD * AREA; // F_drag = k v²
  switch (goal) {
    case "energy": {
      const mu = 0.05, vBar = 10;
      const e = mu * mass * G * L + dragK * vBar * vBar * L;
      return {
        mu, label: "Energy", unit: "kJ", value: e / 1000,
        params: `μ ≈ 0.05 · cruise ≈ 10 m/s · coast every downhill · never brake hard`,
        why: "Engine work mostly fights friction (μ·m·g·L) and drag (∝ v²·L). Low μ and modest speed minimise both; braking throws away energy you already paid for.",
      };
    }
    case "time": {
      const mu = 0.6;
      const vCurve = Math.sqrt(G * 34 * (1 + mu)) * 0.95; // tightest curve at 95%
      const tEst = L / 26; // straights ~35 m/s, curves ~vCurve → avg ≈ 26 m/s
      return {
        mu, label: "Time", unit: "s", value: tEst,
        params: `μ ≈ 0.60 · full throttle on straights · cross curves at ~95% of v_max (≈ ${vCurve.toFixed(0)} m/s at the tightest bend)`,
        why: "High μ lifts every curve limit (v_max = √(g·r·(cosθ+μ))) — you buy cornering speed with friction losses, a worthwhile trade for time.",
      };
    }
    case "risk": {
      return {
        mu: 0.5, label: "Risk time", unit: "s", value: 0,
        params: `μ ≈ 0.50 · cross every curve at ≤ 85% of v_max · brake BEFORE curves, never inside`,
        why: "Zero seconds above 90% grip is achievable: compute v_max, stay at 85%, and all margin stays positive the whole lap.",
      };
    }
    case "wear":
    default: {
      const mu = 0.05;
      const w = mu * mass * G * L;
      return {
        mu, label: "Wear", unit: "kJ", value: w / 1000,
        params: `μ ≈ 0.05 · smooth speeds (wear ∝ μ·N·distance) · gentle on curves so N stays low`,
        why: "Wear is friction work: μ·N per metre. Minimum μ and smooth driving minimise it — but respect the lower curve limits that come with low grip!",
      };
    }
  }
}

const GOAL_META = {
  energy: { icon: "⚡", name: "Least Energy", metric: "energy", unit: "kJ", scale: 1 / 1000 },
  time: { icon: "⏱", name: "Least Time", metric: "time", unit: "s", scale: 1 },
  risk: { icon: "⚠", name: "Least Risk", metric: "riskTime", unit: "s", scale: 1 },
  wear: { icon: "🛞", name: "Least Wear", metric: "wear", unit: "kJ", scale: 1 / 1000 },
};

export default function ChallengeSplash() {
  const goal = useGame((s) => s.challengeGoal);
  const metrics = useGame((s) => s.challengeMetrics);
  const globalTimer = useGame((s) => s.globalTimer);
  const challengeMu = useGame((s) => s.challengeMu);
  const mass = useGame((s) => s.mass);
  const crashes = useGame((s) => s.crashes);
  const reset = useGame((s) => s.reset);
  const exitChallenge = useGame((s) => s.exitChallenge);
  const startChallenge = useGame((s) => s.startChallenge);
  const startGame = useGame((s) => s.startGame);
  const openChallengeSelect = useGame((s) => s.openChallengeSelect);

  const meta = GOAL_META[goal] || GOAL_META.energy;
  const opt = optimum(goal, mass);

  const achievedRaw =
    meta.metric === "time" ? globalTimer : metrics[meta.metric] || 0;
  const achieved = achievedRaw * meta.scale;
  const ratio = opt.value > 0 ? achieved / opt.value : achieved === 0 ? 1 : Infinity;
  const grade =
    crashes > 0 ? "💥 Crashed — the optimum assumes a clean lap"
      : ratio <= 1.15 ? "🏆 OPTIMAL — within 15% of the physics limit!"
        : ratio <= 1.6 ? "✓ Good — see the optimum parameters below"
          : "Keep optimizing — compare your parameters with the optimum";

  const fmt = (n, d = 1) => Number(n).toFixed(d);

  const retry = () => { startChallenge(goal); startGame(); };
  const changeGoal = () => { exitChallenge(); reset(); openChallengeSelect(); };
  const exit = () => { exitChallenge(); reset(); };

  return (
    <div
      data-testid="challenge-splash"
      style={{
        position: "absolute", inset: 0, zIndex: 30,
        background: "rgba(2,4,12,0.94)", backdropFilter: "blur(12px)",
        overflowY: "auto", display: "flex", alignItems: "flex-start", justifyContent: "center",
        color: "#fff", fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div style={{ width: "min(640px, 100%)", padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 13, letterSpacing: 6, color: "#ffd23f", fontWeight: 600 }}>
          🏁 CHALLENGE COMPLETE
        </div>
        <h1 style={{ fontSize: 44, margin: "10px 0 4px", fontWeight: 900, background: "linear-gradient(90deg,#ffd23f,#ff8a3d)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          {meta.icon} {meta.name}
        </h1>
        <div style={{ color: "#cfd6e3", fontSize: 15, marginBottom: 24 }}>{grade}</div>

        {/* Your result vs optimum */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 22 }}>
          <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 14, padding: 18 }}>
            <div style={{ fontSize: 11, color: "#9aa4b2", letterSpacing: 1.5 }}>YOUR {opt.label.toUpperCase()}</div>
            <div style={{ fontSize: 30, fontWeight: 900, color: "#ffd23f" }}>
              {fmt(achieved)} {meta.unit}
            </div>
            <div style={{ fontSize: 12, color: "#9aa4b2", marginTop: 4 }}>with μ = {challengeMu.toFixed(2)}{crashes > 0 ? ` · ${crashes} crash${crashes > 1 ? "es" : ""}` : " · clean lap"}</div>
          </div>
          <div style={{ background: "rgba(0,255,170,0.07)", border: "1px solid rgba(0,255,170,0.3)", borderRadius: 14, padding: 18 }}>
            <div style={{ fontSize: 11, color: "#9aa4b2", letterSpacing: 1.5 }}>PHYSICS OPTIMUM (est.)</div>
            <div style={{ fontSize: 30, fontWeight: 900, color: "#00ffaa" }}>
              {fmt(opt.value)} {meta.unit}
            </div>
            <div style={{ fontSize: 12, color: "#9aa4b2", marginTop: 4 }}>with μ ≈ {opt.mu.toFixed(2)}</div>
          </div>
        </div>

        {/* All metrics strip */}
        <div style={{ background: "rgba(0,0,0,0.4)", borderRadius: 14, padding: "14px 18px", marginBottom: 22, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>
          <span>⚡ {fmt(metrics.energy / 1000)} kJ</span>
          <span>🛞 {fmt(metrics.wear / 1000)} kJ</span>
          <span>⚠ {fmt(metrics.riskTime)} s risky</span>
          <span>⏱ {fmt(globalTimer)} s</span>
          <span>💥 {crashes}</span>
        </div>

        {/* The "answer key": optimal parameters splash */}
        <div style={{ background: "rgba(0,255,170,0.06)", border: "1px solid rgba(0,255,170,0.3)", borderRadius: 14, padding: "18px 20px", marginBottom: 26, textAlign: "left" }}>
          <div style={{ color: "#00ffaa", fontWeight: 800, letterSpacing: 1, fontSize: 13, marginBottom: 8 }}>
            🎯 OPTIMAL PARAMETERS YOU COULD HAVE CHOSEN
          </div>
          <div style={{ fontSize: 14.5, color: "#e8ecf3", lineHeight: 1.7, fontFamily: "'JetBrains Mono', monospace" }}>
            {opt.params}
          </div>
          <div style={{ fontSize: 13, color: "#9fe8c9", lineHeight: 1.65, marginTop: 10 }}>
            {opt.why}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button onClick={retry}
            style={{ flex: 1, minWidth: 150, padding: "14px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#ffd23f,#ff8a3d)", color: "#1a0d00", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>
            ↻ Same goal, beat it
          </button>
          <button onClick={changeGoal}
            style={{ flex: 1, minWidth: 150, padding: "14px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.08)", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>
            🎯 Different goal
          </button>
          <button onClick={exit}
            style={{ flex: 1, minWidth: 150, padding: "14px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.08)", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>
            ← Main menu
          </button>
        </div>
      </div>
    </div>
  );
}
